import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  DEFAULT_CLIENT,
  getClienteApiUrl,
  getClientImagePath,
  getContentBasePath,
} from './client-path.util';

@Injectable({ providedIn: 'root' })
export class ClientLoaderService {
  private clientName = DEFAULT_CLIENT;
  private imageFolder = DEFAULT_CLIENT;
  private clientData: Record<string, string> = {};
  private clientLoaded = false;
  private clientLoadedPromise!: Promise<void>;
  private resolveClientLoaded!: () => void;

  constructor(private http: HttpClient) {
    this.clientLoadedPromise = new Promise(resolve => {
      this.resolveClientLoaded = resolve;
    });
  }

  async loadClient(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<Record<string, string>>(getClienteApiUrl())
      );
      const clientName = response['name'] || response['carpeta'] || DEFAULT_CLIENT;
      const themeFallback = await this.fetchThemeFile(clientName);
      this.procesarDatosCliente({ ...themeFallback, ...response });
    } catch (error) {
      console.warn('Backend no responde. Activando respaldo estatico...', error);
      const storedName = localStorage.getItem('clientName');
      const storedCarpeta = localStorage.getItem('imageFolder');
      this.clientName = storedName || DEFAULT_CLIENT;
      this.imageFolder = storedCarpeta || storedName || DEFAULT_CLIENT;
      await this.loadThemeFromFile(this.clientName);
    } finally {
      this.clientLoaded = true;
      this.resolveClientLoaded();
    }
  }

  private procesarDatosCliente(data: Record<string, string>): void {
    this.clientData = { ...data };
    this.clientName = data['name'] || data['carpeta'] || DEFAULT_CLIENT;
    this.imageFolder = data['carpeta'] || this.clientName;
    const favicon = data['favicon'] || 'favicon.ico';
    this.setFavicon(this.imageFolder, favicon);
    this.setDocumentTitle(data['title-head-bo']);
    localStorage.setItem('clientName', this.clientName);
    localStorage.setItem('imageFolder', this.imageFolder);
    localStorage.setItem('login-type', data['login-type-bo'] ?? 'both');
    this.inyectarEstilos(data, this.imageFolder);
  }

  private async fetchThemeFile(clientName: string): Promise<Record<string, string>> {
    const ruta = `${getContentBasePath()}/themes/${clientName}.json`;
    try {
      return await firstValueFrom(this.http.get<Record<string, string>>(ruta));
    } catch {
      return {};
    }
  }

  private async loadThemeFromFile(clientName: string): Promise<void> {
    const themeData = await this.fetchThemeFile(clientName);
    if (Object.keys(themeData).length === 0) {
      console.error('Error critico: fallo backend y JSON de respaldo.', clientName);
      if (clientName !== 'default') {
        await this.loadThemeFromFile('default');
      }
      return;
    }
    themeData['name'] = clientName;
    this.procesarDatosCliente(themeData);
  }

  private normalizeTheme(data: Record<string, string>): Record<string, string> {
    const normalized = { ...data };
    const aliasPairs: Array<[string, string]> = [
      ['--blue', '--accent'],
      ['--blue-light', '--accent-light'],
      ['--blue-pale', '--accent-pale'],
      ['--blue-paler', '--accent-paler'],
    ];
    for (const [legacy, semantic] of aliasPairs) {
      if (normalized[legacy] && !normalized[semantic]) {
        normalized[semantic] = normalized[legacy];
      }
      if (normalized[semantic] && !normalized[legacy]) {
        normalized[legacy] = normalized[semantic];
      }
    }
    return normalized;
  }

  private inyectarEstilos(themeData: Record<string, string>, imageFolder: string): void {
    const root = document.documentElement;
    const theme = this.normalizeTheme(themeData);

    Object.entries(theme).forEach(([key, value]) => {
      if (key.startsWith('--url-')) {
        const encodedFileName = key.startsWith('--url--manual') ? value : encodeURIComponent(value);
        root.style.setProperty(key, `url('${getClientImagePath(imageFolder, encodedFileName)}')`);
      } else if (key.startsWith('--')) {
        root.style.setProperty(key, value);
      }
    });
  }

  async getClientName(): Promise<string> {
    if (!this.clientLoaded) {
      await this.clientLoadedPromise;
    }
    return this.clientName;
  }

  getImageFolder(): string {
    return this.imageFolder;
  }

  async getConfig(key: string, fallback = ''): Promise<string> {
    if (!this.clientLoaded) {
      await this.clientLoadedPromise;
    }
    return this.clientData[key] ?? fallback;
  }

  getConfigSync(key: string, fallback = ''): string {
    return this.clientData[key] ?? fallback;
  }

  getImageUrl(fileName: string, imageFolder?: string): string {
    return getClientImagePath(imageFolder ?? this.imageFolder, fileName);
  }

  getHeaderLogoUrl(): string {
    const fileName = this.getConfigSync('--url-logo-header', 'logo4.png');
    return this.getImageUrl(fileName);
  }

  getLoginType(): string {
    return localStorage.getItem('login-type') ?? 'both';
  }

  isVpnActiva(): boolean {
    const v = this.getConfigSync('vpnActiva', 'false').trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }

  setFavicon(imageFolder: string, fileName = 'favicon.ico'): void {
    const head = document.head;
    let link: HTMLLinkElement | null = head.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      head.appendChild(link);
    }
    link.href = getClientImagePath(imageFolder, fileName);
  }

  private setDocumentTitle(title?: string): void {
    if (!title) {
      return;
    }
    document.title = title;
    const titleEl = document.getElementById('tituloDoc');
    if (titleEl) {
      titleEl.innerHTML = title;
    }
  }
}